import React, { useState, useMemo, useEffect } from 'react';
import {
  List, MapPin, Hotel, FileCheck, Star,
  Clock, TrendingUp, Users, Leaf, ArrowLeft, Navigation, Search,
  GitCompareArrows,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTrips } from '../hooks/useTrips';
import { useTeamScope, filterTripsByScope } from '../hooks/useTeamScope';
import PermissionGate from '../components/shared/PermissionGate';
import { PERMISSIONS } from '../utils/permissions';
import AllTravelReport from '../components/reports/AllTravelReport';
import AvgSpendByDestination from '../components/reports/AvgSpendByDestination';
import SpendByDepartureCity from '../components/reports/SpendByDepartureCity';
import HotelPopularity from '../components/reports/HotelPopularity';
import TravelPolicy from '../components/reports/TravelPolicy';
import ProviderRatings from '../components/reports/ProviderRatings';
import ApprovalTurnaroundReport from '../components/reports/ApprovalTurnaroundReport';
import LeadTimeTrendReport from '../components/reports/LeadTimeTrendReport';
import SupportWorkerReport from '../components/reports/SupportWorkerReport';
import CarbonReport from '../components/reports/CarbonReport';
import TravelDayLookupReport from '../components/reports/TravelDayLookupReport';
import VTOReconciliationReport from '../components/reports/VTOReconciliationReport';

const REPORT_GROUPS = [
  {
    label: 'Spend & Travel',
    reports: [
      {
        key: 'all_travel',
        label: 'All Travel',
        Icon: List,
        description: 'Complete trip list with filters for status, type, cost centre, and date range. Includes booking window and CSV export.',
        color: 'teal',
      },
      {
        key: 'avg_dest',
        label: 'Avg Spend by Destination',
        Icon: MapPin,
        description: 'Average and total trip cost grouped by destination city, with expandable sector breakdown. Useful for benchmarking and identifying high-cost destinations.',
        color: 'teal',
      },
      {
        key: 'departure',
        label: 'Spend by Departure City',
        Icon: Navigation,
        description: 'Total and average spend grouped by departure city. Understand where most travel originates and compare costs across locations.',
        color: 'teal',
      },
      {
        key: 'hotel',
        label: 'Hotel Popularity',
        Icon: Hotel,
        description: 'Hotels ranked by booking frequency within each destination, with average nightly rates. Identify preferred suppliers and support rate negotiation.',
        color: 'teal',
      },
    ],
  },
  {
    label: 'Workflow & Quality',
    reports: [
      {
        key: 'approval_turnaround',
        label: 'Approval Turnaround',
        Icon: Clock,
        description: 'How long trips spend in pending approval before being actioned. Identifies slow approval patterns and bottlenecks by approver and trip type.',
        color: 'indigo',
      },
      {
        key: 'lead_time',
        label: 'Lead Time Trend',
        Icon: TrendingUp,
        description: 'Month-over-month booking lead time trends. Short lead times cost more — use this to advocate for earlier trip submissions.',
        color: 'indigo',
      },
      {
        key: 'travel_day_lookup',
        label: 'Travel Day Lookup',
        Icon: Search,
        description: 'Find all travellers on a specific date, or all passengers on the same flight. Search by date or flight number with optional departure/arrival filters.',
        color: 'indigo',
      },
      {
        key: 'provider_ratings',
        label: 'Provider Ratings',
        Icon: Star,
        description: 'Anonymised ratings submitted by travellers after their trips. Covers accessibility, service quality, and experience across airlines, hotels, and other providers.',
        color: 'indigo',
        global: true,
      },
    ],
  },
  {
    label: 'People & Compliance',
    reports: [
      {
        key: 'policy',
        label: 'Travel Policy',
        Icon: FileCheck,
        description: 'Actual accommodation and flight spend vs configured policy limits by city. Flags bookings that exceed thresholds for compliance tracking.',
        color: 'amber',
        policyGated: true,
      },
      {
        key: 'support_worker',
        label: 'Support Worker',
        Icon: Users,
        description: 'Travel spend associated with support workers and carers, proportionally estimated by trip. Required for NDIS grant acquittals and service costing.',
        color: 'amber',
      },
      {
        key: 'carbon',
        label: 'Carbon Footprint',
        Icon: Leaf,
        description: 'Estimated CO₂ equivalent emissions from flights using DEFRA 2023 factors. Covers economy through first class across short-haul and long-haul routes.',
        color: 'amber',
        carbonGated: true,
      },
    ],
  },
  {
    label: 'STX Internal',
    reports: [
      {
        key: 'vto_reconciliation',
        label: 'VTO Reconciliation',
        Icon: GitCompareArrows,
        description: 'Upload a Balance Summary Audit export to compare VTO trip amounts against portal totals. Highlights mismatches and trips missing a VTO reference.',
        color: 'slate',
        stxOnly: true,
        global: true,
      },
    ],
  },
];

const COLOR_CLASSES = {
  teal:   { icon: 'bg-teal-50 text-teal-600',     border: 'hover:border-teal-300' },
  indigo: { icon: 'bg-indigo-50 text-indigo-600', border: 'hover:border-indigo-300' },
  amber:  { icon: 'bg-amber-50 text-amber-600',   border: 'hover:border-amber-300' },
  slate:  { icon: 'bg-slate-100 text-slate-600',  border: 'hover:border-slate-400' },
};

export default function Reports() {
  useEffect(() => {
    document.title = 'Reports — STX Connect';
  }, []);

  const { userProfile } = useAuth();
  const { clientId: tenantClientId, activeClientId, isSTX, clientConfig, activeClientConfig } = useTenant();
  const [activeReport, setActiveReport] = useState(null);

  const clientId = isSTX ? activeClientId : tenantClientId;
  const effectiveConfig = isSTX ? activeClientConfig : clientConfig;

  const { trips, loading } = useTrips(clientId, isSTX, isSTX ? activeClientId : null);
  const scope = useTeamScope(userProfile, clientId);
  const scopedTrips = useMemo(
    () => filterTripsByScope(trips, scope, userProfile),
    [trips, scope, userProfile]
  );

  const showPolicyTab  = effectiveConfig?.features?.accommodationPolicy !== false
    || effectiveConfig?.features?.flightPolicy === true;
  const showCarbonTab  = effectiveConfig?.features?.carbonTracking === true;

  // Reset to picker if we switch client and the active report is no longer valid
  const activeIsGlobal = REPORT_GROUPS.flatMap(g => g.reports).find(r => r.key === activeReport)?.global;
  const needsClient = isSTX && !activeClientId && !activeIsGlobal;

  function isVisible(r) {
    if (r.stxOnly  && !isSTX)         return false;
    if (r.policyGated && !showPolicyTab) return false;
    if (r.carbonGated && !showCarbonTab) return false;
    return true;
  }

  const currentReport = activeReport
    ? REPORT_GROUPS.flatMap(g => g.reports).find(r => r.key === activeReport)
    : null;

  function renderReport() {
    if (activeReport === 'provider_ratings') return <ProviderRatings />;
    if (loading) return <div className="text-center py-12 text-gray-600 text-sm">Loading trips…</div>;
    if (activeReport === 'all_travel')          return <AllTravelReport        trips={scopedTrips} userProfile={userProfile} />;
    if (activeReport === 'avg_dest')            return <AvgSpendByDestination  trips={scopedTrips} />;
    if (activeReport === 'departure')           return <SpendByDepartureCity   trips={scopedTrips} />;
    if (activeReport === 'hotel')               return <HotelPopularity        trips={scopedTrips} />;
    if (activeReport === 'policy')              return <TravelPolicy            trips={scopedTrips} clientId={clientId} isSTX={isSTX} clientConfig={effectiveConfig} />;
    if (activeReport === 'approval_turnaround') return <ApprovalTurnaroundReport trips={scopedTrips} />;
    if (activeReport === 'lead_time')           return <LeadTimeTrendReport     trips={scopedTrips} />;
    if (activeReport === 'support_worker')      return <SupportWorkerReport     trips={scopedTrips} />;
    if (activeReport === 'carbon')              return <CarbonReport            trips={scopedTrips} clientConfig={effectiveConfig} />;
    if (activeReport === 'travel_day_lookup')  return <TravelDayLookupReport    trips={scopedTrips} />;
    if (activeReport === 'vto_reconciliation') return <VTOReconciliationReport  trips={trips} />;
    return null;
  }

  return (
    <PermissionGate permission={PERMISSIONS.REPORT_VIEW}>
      <div>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          {activeReport && (
            <button
              onClick={() => setActiveReport(null)}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 -ml-1"
            >
              <ArrowLeft size={15} aria-hidden="true" /> All reports
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-800">
            {currentReport ? currentReport.label : 'Reports'}
          </h1>
        </div>

        {/* Report view */}
        {activeReport ? (
          needsClient ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-600">
              <p className="text-sm">Select a client from the top bar to view this report.</p>
            </div>
          ) : (
            renderReport()
          )
        ) : (
          /* Card picker */
          <div className="space-y-8">
            {isSTX && !activeClientId && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                Select a client from the top bar to run client-specific reports. Provider Ratings is available without a client selected.
              </div>
            )}
            {REPORT_GROUPS.map(group => {
              const visibleReports = group.reports.filter(isVisible);
              if (!visibleReports.length) return null;
              return (
                <div key={group.label}>
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">{group.label}</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {visibleReports.map(report => {
                      const colors = COLOR_CLASSES[report.color] || COLOR_CLASSES.teal;
                      const disabled = isSTX && !activeClientId && !report.global;
                      return (
                        <button
                          key={report.key}
                          onClick={() => !disabled && setActiveReport(report.key)}
                          disabled={disabled}
                          className={`text-left bg-white rounded-xl border border-gray-200 p-5 transition-all group ${
                            disabled
                              ? 'opacity-40 cursor-not-allowed'
                              : `${colors.border} hover:shadow-sm cursor-pointer`
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded-lg shrink-0 ${colors.icon}`}>
                              <report.Icon size={15} aria-hidden="true" />
                            </div>
                            <span className="text-sm font-semibold text-gray-800 leading-tight">{report.label}</span>
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">{report.description}</p>
                          {!disabled && (
                            <p className={`text-xs font-medium mt-3 ${colors.icon.split(' ')[1]}`}>Open →</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PermissionGate>
  );
}
